import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { initAnalytics, trackPageView } from '@/utils/deferredAnalytics';
import { trackPageView as internalTrackPageView } from '@/utils/analytics';

/**
 * Hook to handle all analytics tracking:
 * - Initializes Google Analytics after app becomes interactive
 * - Tracks page views for both internal and Google Analytics
 * - Prevents double-tracking on the same route
 */
export function usePageTracking() {
  const location = useLocation();
  const lastTrackedPath = useRef('');
  const analyticsInitialized = useRef(false);
  
  // Initialize Google Analytics once (deferred)
  useEffect(() => {
    if (analyticsInitialized.current) return;
    analyticsInitialized.current = true;
    
    // Use requestIdleCallback for non-blocking initialization
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        initAnalytics();
      }, { timeout: 3000 });
    } else {
      setTimeout(initAnalytics, 2000);
    }
  }, []);
  
  // Track page views on route change
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip if same page (prevents double tracking)
    if (lastTrackedPath.current === currentPath) return;
    lastTrackedPath.current = currentPath;
    
    // Skip admin pages
    if (currentPath.startsWith('/admin')) return;
    
    // Track with both internal and Google Analytics (async, non-blocking)
    const track = () => {
      // Internal analytics (to our backend)
      internalTrackPageView(currentPath);
      
      // Google Analytics
      trackPageView(currentPath);
    };
    
    // Use requestIdleCallback for non-blocking tracking
    if ('requestIdleCallback' in window) {
      requestIdleCallback(track);
    } else {
      setTimeout(track, 0);
    }
    
  }, [location.pathname]);
  
  return null;
}

export default usePageTracking;
